import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  InProgressUtilityProgramEnrollment,
  selectInProgressEnrollment,
} from '@/reducers/inProgressUtilityProgramEnrollments'

import { UtilityProgram } from '@/types/utilityProgram'
import {
  ClientRequirements,
  ProgramRequirementProps,
  ProgramRequirementStep,
  requirementNameConfig,
  RequirementStepConfig,
} from '@/types/utilityProgramProfileEligibility'
import { useNavigation } from '@/app/hooks/useNavigation'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import { programsNewCollection } from '@/reducers/programsNew'
import { ProfileEligibilityRequirements } from '@/types/programs-new/profile-requirements'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { TransformedViewConfig } from '@/generated/graphql'
import { isDocumentEmpty } from '@/graphql/utilties/document'
import {
  isDeviceSelectionCompleted,
  isUtilityAccountNumberCompleted,
} from '@/app/pages/multi-program-enrollment/utils/utils'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { ProfileRequirementsResult } from '@/types/programs-new/programs-new'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import { postApplicationProgress } from '@/reducers/utilityProgramApplications'

export const useProgramRequirements = (utilityProgram: UtilityProgram) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigation()
  const { closeBottomSheet } = useBottomSheet()
  const { onSaveForLater, routePrefix } = useCurrentMultiProgramFlowContext()

  const { data, isLoading } = programsNewCollection.useFetch()
  const inProgressEnrollment = useAppSelector(
    selectInProgressEnrollment(utilityProgram.id),
  )
  const { data: utilityAccountData } = utilityAccountsCollection.useFetch(
    '/utilities/utility_account',
  )
  const existingAccountNumber =
    (utilityAccountData?.[0]?.account_number as string | undefined) ?? null

  const [programRequirements, setProgramRequirements] =
    useState<ProgramRequirementProps | null>(null)

  const [requirementData, setRequirementData] = useState<
    ProfileRequirementsResult[]
  >([])

  const createPath = (path: string) => {
    return `${routePrefix}${path}`
  }

  // effect for updating plan requirements based on state changes from server & local client
  useEffect(() => {
    if (data.length === 0 && !isLoading) {
      setProgramRequirements(null)
      setRequirementData([])
      return
    }
    const program = data.find((d) => d.utility_program.id === utilityProgram.id)

    // Get the survey requirements from the program by mapping each of the required
    // enrollment survey responses to a ProfileRequirementsResult
    const surveyRequirements = (
      program?.utility_program.required_enrollment_survey_responses ?? []
    ).map(
      (r) =>
        ({
          key: ProfileEligibilityRequirements.RequireSurvey,
          name:
            (
              r.survey.metadata?.view_config as {
                application_step_header?: string
              }
            )?.application_step_header ?? '',
          is_eligible: !!r.completed,
          surveyType: r.survey.type,
        } as ProfileRequirementsResult),
    )

    // Combine the survey requirements with the remaining requirement_results
    const requirements = [
      ...(program?.device_eligibility_overview.profile.requirements_result.filter(
        (r) => r.key !== ProfileEligibilityRequirements.RequireSurvey,
      ) ?? []),
      ...surveyRequirements,
    ]

    const viewConfig = data.find(
      (d) => d.utility_program.id === utilityProgram.id,
    )?.view_config_json

    if (!requirements || requirements.length === 0) {
      return
    }

    const serverSteps = serverRequirementsToClientRequirements(
      requirements,
      utilityProgram,
    )

    const steps = addClientSteps(
      serverSteps,
      utilityProgram.id,
      inProgressEnrollment,
      viewConfig,
      existingAccountNumber,
      requirements,
    )

    const orderedSteps = orderRequirementSteps(steps)

    setProgramRequirements({
      step: orderedSteps.filter((step) => step.completed).length,
      numberOfSteps: orderedSteps.length,
      steps: orderedSteps,
      planName: utilityProgram.view_config_json?.name ?? utilityProgram.name,
    })
    setRequirementData(requirements)
  }, [data, inProgressEnrollment, isLoading, existingAccountNumber])

  const navigateTo = (path: string) => {
    navigate.push(createPath(path))
  }

  const completeEnrollment = () => {
    closeBottomSheet()
    navigate.push(`/devices`)
  }

  const saveForLater = () => {
    dispatch(programsNewCollection.actions.invalidate())
    closeBottomSheet()

    onSaveForLater()
  }

  const postStepProgress = () => {
    if (!programRequirements) {
      return
    }
    return postApplicationProgress(
      dispatch,
      utilityProgram.id,
      programRequirements,
    )
  }

  const routeToStep = (step: ProgramRequirementStep) => {
    navigateTo(step.path)
    closeBottomSheet()
    postStepProgress()
  }

  const routeToNextUncompletedStep = () => {
    const nextStep = programRequirements?.steps.filter(
      (step) => !step.completed,
    )?.[0]
    if (!nextStep) {
      return
    }
    navigateTo(nextStep.path)
    closeBottomSheet()
    postStepProgress()
  }

  const routeToNextStep = () => {
    if (!programRequirements) {
      return
    }
    const currentStepIndex = programRequirements.steps.findIndex((s) =>
      location.pathname.includes(s.path),
    )
    const nextStep = programRequirements.steps[currentStepIndex + 1]
    if (!nextStep) {
      return
    }
    navigateTo(nextStep.path)
    closeBottomSheet()
    postStepProgress()
  }

  const routeToPreviousStep = () => {
    if (!programRequirements) {
      return
    }
    const currentStepIndex = programRequirements.steps.findIndex((s) =>
      location.pathname.includes(s.path),
    )
    if (currentStepIndex < 1) {
      navigate.goBack()
      return
    }
    const previousStep = programRequirements.steps[currentStepIndex - 1]
    navigate.push(previousStep.path)
  }

  const hasExternalUtilityConnectionRequirement = useMemo(
    () =>
      requirementData
        .map((req) => req.key)
        .includes(
          ProfileEligibilityRequirements.ExternalUtilityConnectionAttempt,
        ),
    [requirementData],
  )
  const hasCompletedExternalUtilityConnectionRequirement = useMemo(
    () =>
      requirementData
        .map((req) => req)
        .find(
          (k) =>
            k.key ===
            ProfileEligibilityRequirements.ExternalUtilityConnectionAttempt,
        )?.is_eligible ?? false,
    [requirementData],
  )

  return {
    saveForLater,
    completeEnrollment,
    programRequirements,
    routeToStep,
    routeToNextStep,
    routeToPreviousStep,
    routeToNextUncompletedStep,
    isLoading,
    requirementData,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
  }
}

function orderRequirementSteps(steps: ProgramRequirementProps['steps']) {
  return orderRequirementStepAfterOtherStep(
    steps,
    ProfileEligibilityRequirements.RequireSurvey,
    ClientRequirements.SelectDevice,
  )
}

function orderRequirementStepAfterOtherStep(
  steps: ProgramRequirementProps['steps'],
  stepKey: string,
  afterStepKey: string,
) {
  const stepsToMove = steps.filter((step) => step.key === stepKey)
  const stepsWithout = steps.filter((step) => step.key !== stepKey)
  const afterStepIndex = stepsWithout.findIndex(
    (step) => step.key === afterStepKey,
  )

  if (afterStepIndex === -1 || stepsToMove.length === 0) {
    // If afterStepKey not found or nothing to move, return as is
    return steps
  }

  return [
    ...stepsWithout.slice(0, afterStepIndex + 1),
    ...stepsToMove,
    ...stepsWithout.slice(afterStepIndex + 1),
  ]
}

export function serverRequirementsToClientRequirements(
  requirements: ProfileRequirementsResult[],
  utilityProgram: UtilityProgram,
) {
  return requirements
    .filter((req) => requirementNameConfig.get(req.key))
    .map((req) => {
      const config = requirementNameConfig.get(req.key) as RequirementStepConfig
      let completed = req.is_eligible
      // Utility step has an optional secondary step of setting plan.
      // This checks for that to be completed
      if (
        req.key === ProfileEligibilityRequirements.RequireUtility &&
        completed
      ) {
        const planRequirement = requirements.find(
          (req) => req.key === ProfileEligibilityRequirements.RequirePlan,
        )
        if (planRequirement) {
          completed = planRequirement.is_eligible
        }
      }

      // If a utility connection requirement exists, verfiy it has been completed
      // before setting RequireUtility as eligible
      const utilityConnectionRequirement = requirements.find(
        (r) =>
          r.key ===
          ProfileEligibilityRequirements.ExternalUtilityConnectionAttempt,
      )
      if (
        req.key === ProfileEligibilityRequirements.RequireUtility &&
        completed &&
        utilityConnectionRequirement
      ) {
        completed = utilityConnectionRequirement.is_eligible
      }

      return {
        name:
          req.key === ProfileEligibilityRequirements.RequireSurvey
            ? req.name
            : config.name,
        path: `/multi-program-enrollment/${utilityProgram.id}/${config.path}${
          req.surveyType ? `/${req.surveyType}` : ''
        }`,
        key: req.key as ProfileEligibilityRequirements,
        completed,
        // field for later
        isNew: false,
      }
    })
}

function addClientSteps(
  serverSteps: ProgramRequirementProps['steps'],
  programId: number,
  program?: InProgressUtilityProgramEnrollment,
  viewConfig?: TransformedViewConfig,
  existingAccountNumber?: string | null,
  requirements?: ProfileRequirementsResult[],
) {
  if (serverSteps.length === 0) {
    return []
  }
  serverSteps = addDeviceStep(serverSteps, programId, program)
  serverSteps = addUtilityAccountNumberStep(
    serverSteps,
    programId,
    program,
    viewConfig,
    existingAccountNumber,
    requirements,
  )
  if (!isDocumentEmpty(viewConfig?.tosDocument) || viewConfig?.tosUrl) {
    serverSteps = addReviewTOSStep(
      serverSteps,
      programId,
      program?.tosAccepted ?? false,
    )
  }
  serverSteps = addEnrollmentStep(serverSteps, programId)
  return serverSteps
}

function addDeviceStep(
  serverSteps: ProgramRequirementProps['steps'],
  programId: number,
  program?: InProgressUtilityProgramEnrollment,
) {
  const deviceRequirement = serverSteps.find(
    (step) => ProfileEligibilityRequirements.Device === step.key,
  )
  serverSteps = serverSteps.filter(
    (s) => s.key !== ProfileEligibilityRequirements.Device,
  )
  if (!deviceRequirement) {
    return serverSteps
  }
  const stepConfig = requirementNameConfig.get(
    ProfileEligibilityRequirements.Device,
  )
  if (!stepConfig) {
    return serverSteps
  }
  const addDeviceStep = {
    name: stepConfig.name,
    path: `/multi-program-enrollment/${programId}/${stepConfig.path}`,
    completed: program ? isDeviceSelectionCompleted(program) : false,
    isNew: false,
    key: ClientRequirements.SelectDevice,
    clientStep: true,
  } satisfies ProgramRequirementStep
  return serverSteps.toSpliced(serverSteps.length, 0, addDeviceStep)
}

function addEnrollmentStep(
  serverSteps: ProgramRequirementProps['steps'],
  programId: number,
) {
  const stepConfig = requirementNameConfig.get(
    ClientRequirements.ProgramEnrollment,
  )
  if (!stepConfig) {
    return serverSteps
  }
  const programEnrollmentStep = {
    name: stepConfig.name,
    path: `/multi-program-enrollment/${programId}/${stepConfig.path}`,
    completed: false,
    isNew: false,
    key: ClientRequirements.ProgramEnrollment,
    clientStep: true,
  } satisfies ProgramRequirementStep
  return serverSteps.toSpliced(serverSteps.length, 0, programEnrollmentStep)
}

function addUtilityAccountNumberStep(
  serverSteps: ProgramRequirementProps['steps'],
  programId: number,
  program?: InProgressUtilityProgramEnrollment,
  viewConfig?: TransformedViewConfig,
  existingAccountNumber?: string | null,
  requirements?: ProfileRequirementsResult[],
) {
  // Check if there's a REQUIRE_UTILITY_ACCOUNT_NUMBER requirement from server
  const hasUtilityAccountNumberRequirement =
    requirements?.some(
      (req) =>
        req.key === ProfileEligibilityRequirements.RequireUtilityAccountNumber,
    ) ?? false

  if (!hasUtilityAccountNumberRequirement) {
    return serverSteps
  }

  const stepConfig = requirementNameConfig.get(
    ClientRequirements.EnterUtilityAccountNumber,
  )
  if (!stepConfig) {
    return serverSteps
  }

  // Check completion based on in-progress enrollment or existing account number
  const accountNumberLength =
    viewConfig?.accountNumberEntry?.accountNumberLength ?? null
  const completed = isUtilityAccountNumberCompleted(
    program,
    existingAccountNumber ?? null,
    accountNumberLength ?? undefined,
  )

  const utilityAccountNumberStep = {
    name: stepConfig.name,
    path: `/multi-program-enrollment/${programId}/${stepConfig.path}`,
    completed,
    isNew: false,
    key: ClientRequirements.EnterUtilityAccountNumber,
    clientStep: true,
  } satisfies ProgramRequirementStep

  // Remove server requirement if it exists (we're replacing it with client step)
  const filteredSteps = serverSteps.filter(
    (s) => s.key !== ProfileEligibilityRequirements.RequireUtilityAccountNumber,
  )

  return filteredSteps.toSpliced(
    filteredSteps.length,
    0,
    utilityAccountNumberStep,
  )
}

function addReviewTOSStep(
  serverSteps: ProgramRequirementProps['steps'],
  programId: number,
  tosAccepted: boolean,
) {
  const stepConfig = requirementNameConfig.get(ClientRequirements.ReviewTOS)
  if (!stepConfig) {
    return serverSteps
  }
  const reviewTOSStep = {
    name: stepConfig.name,
    path: `/multi-program-enrollment/${programId}/${stepConfig.path}`,
    completed: tosAccepted,
    isNew: false,
    key: ClientRequirements.ReviewTOS,
    clientStep: true,
  } satisfies ProgramRequirementStep
  return serverSteps.toSpliced(serverSteps.length, 0, reviewTOSStep)
}
